import React, { useState, useEffect, useCallback, useRef } from "react";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { useLoading } from "../../../../context/LoadingContext";
import { jwtDecode } from "jwt-decode";
import { getParameterFromManager } from "../../../../services/Utils";
import "../../../../styles/general.css";
import "../../../../styles/amtpl.css";
import DataTable from "../../../../pages/Requirement/components/common/ReusableTable";
import SearchFilter from "../../../../pages/Requirement/components/common/SearchFilter";
import {
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import {
  fetchData,
  searchfetchQueries,
  postQuery,
} from "../../../../services/BackendServices";
import { v4 as uuidv4 } from "uuid";
import AlertModal from "../../../../pages/Requirement/components/common/AlerModel";

const TransferofferCharges = () => {
  const navigate = useNavigate();
  const { setLoading } = useLoading();
  const [rowStart, setRowStart] = useState(0);
  const [pageSize] = useState(5);
  const [totalItems, setTotalItems] = useState(0);
  const [productoffercharges, setProductOfferCharges] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const [searchValues, setSearchValues] = useState({
    searchCategory: "",
    searchProduct: "",
    searchProductGrade: "",
  });
  const [productCategoryList, setProductCategoryList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [allProductList, setAllProductList] = useState([]);
  const [productGradeList, setProductGradeList] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editProductOfferChargesID, setEditProductOfferChargesID] =
    useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
    autoClose: null,
  });

  const formRef = useRef(null);

  const handleModalClose = () => {
    const action = modal.nextAction;
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));
    if (action) action();
  };

  // Form state
  const [formData, setFormData] = useState({
    category: "",
    product: "",
    grade: "",
    agropreneursCharges: "",
    sortingGradingCharges: "",
    financeOperationsCharges: "",
    minOfferQuantity: "",
  });

  // State for errors
  const [errors, setErrors] = useState({});

  const loadProductCategoryList = useCallback(async () => {
    setLoading(true);
    try {
      const [initialResponse] = await Promise.all([
        fetchData({
          target: "listProductCategory",
          userOrder: "CategoryName",
          rowStart: 0,
          pageSize: 1,
        }),
      ]);

      const totalCount = initialResponse?.total || initialResponse?.length || 0;

      if (totalCount > 0) {
        const [allItems] = await Promise.all([
          searchfetchQueries({
            target: "listProductCategory",
            userOrder: "CategoryName",
            rowStart: 0,
            pageSize: totalCount,
          }),
        ]);

        setProductCategoryList(allItems);
      } else {
        setProductCategoryList([]);
      }
    } catch (error) {
      console.error("Error loading listProductCategory: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const loadProductList = useCallback(
    async (categoryId) => {
      setLoading(true);
      try {
        const [allProducts] = await Promise.all([
          searchfetchQueries({
            target: "listProducts",
            userOrder: "ProductName",
            rowStart: 0,
            pageSize: 10000,
            filterTerms: { ProductCategoryID: categoryId },
          }),
        ]);

        setAllProductList(allProducts);
        // Filter distinct products based on ProductName
        const distinctProducts = Array.from(
          new Map(allProducts.map((p) => [p.ProductName, p])).values()
        );
        console.log("@@distinctProducts", distinctProducts);

        setProductList(distinctProducts);
      } catch (error) {
        console.error("Error loading products: ", error);
        setProductList([]);
        setAllProductList([]);
      } finally {
        setLoading(false);
      }
    },
    [setLoading]
  );

  // Handle input change for the form
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Handle cascading dropdown logic
    if (name === "category") {
      // Reset product and grade when category changes
      setFormData((prev) => ({
        ...prev,
        product: "",
        grade: "",
      }));
      setProductList([]);
      setProductGradeList([]);

      // Load products for selected category
      if (value) {
        loadProductList(value);
      }
    } else if (name === "product") {
      // Reset grade when product changes
      setFormData((prev) => ({
        ...prev,
        grade: "",
      }));
      setProductGradeList([]);

      // Load grades for selected product
      if (value) {
        const selectedProduct = productList.find(
          (p) => p.ProductName === value
        );
        if (selectedProduct) {
          const matchingProducts = allProductList.filter(
            (p) => p.ProductName === selectedProduct.ProductName
          );
          setProductGradeList(matchingProducts);
        }
      }
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Check required fields
    if (!formData.category) newErrors.category = "This field is required";
    if (!formData.product) newErrors.product = "This field is required";
    if (!formData.grade) newErrors.grade = "This field is required";
    if (!formData.agropreneursCharges)
      newErrors.agropreneursCharges = "This field is required";
    if (!formData.sortingGradingCharges)
      newErrors.sortingGradingCharges = "This field is required";
    if (!formData.financeOperationsCharges)
      newErrors.financeOperationsCharges = "This field is required";
    if (!formData.minOfferQuantity)
      newErrors.minOfferQuantity = "This field is required";

    // Check numeric fields for charges
    const chargesFields = [
      "agropreneursCharges",
      "sortingGradingCharges",
      "financeOperationsCharges",
    ];
    chargesFields.forEach((field) => {
      if (formData[field]) {
        const value = parseFloat(formData[field].trim());
        if (isNaN(value)) {
          newErrors[field] = "Must be a number";
        } else if (value < 0) {
          newErrors[field] = "Must be non-negative";
        } else if (value > 100) {
          newErrors[field] = "Must be less than or equal to 100";
        }
      }
    });

    // Validate minOfferQuantity as a positive number
    if (formData.minOfferQuantity) {
      const value = parseFloat(formData.minOfferQuantity.trim());
      if (isNaN(value)) {
        newErrors.minOfferQuantity = "Must be a number";
      } else if (value <= 0) {
        newErrors.minOfferQuantity = "Must be positive";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Simulate API call to save form data
  const saveFormData = async (data) => {
    setLoading(true);
    try {
      // Simulate API call with a 1-second delay
      let body = null;
      console.log("Saving data to API:", data);
      if (isEditMode) {
        body = {
          target: "updateProductOfferCharge",
          updateProductOfferChargeInput: {
            ProductOfferChargesID: data.ProductOfferChargesID,
            ProductID: data.productId, // Use the actual ProductID
            AgropreneursCharges: data.agropreneursCharges,
            SortingGradingCharges: data.sortingGradingCharges,
            FinanceOperationsCharges: data.financeOperationsCharges,
            MinOfferQuantityMT: data.minOfferQuantity,
          },
        };
      } else {
        body = {
          target: "createProductOfferCharge",
          createProductOfferChargeInput: {
            ProductOfferChargesID: data.ProductOfferChargesID,
            ProductID: data.productId, // Use the actual ProductID
            AgropreneursCharges: data.agropreneursCharges,
            SortingGradingCharges: data.sortingGradingCharges,
            FinanceOperationsCharges: data.financeOperationsCharges,
            MinOfferQuantityMT: data.minOfferQuantity,
          },
        };
      }

      console.log("Saving body to API:", body);
      if (body !== null) {
        const result = await postQuery(body);
        if (result) {
          setModal({
            show: true,
            type: "success",
            title: "Success",
            message: `Product offer charges ${
              isEditMode ? "updated" : "added"
            } successfully!`,
            autoClose: false,
          });
        }
      }

      // Refresh the table data
      await searchProductOfferCharges(searchValues, rowStart);
    } catch (error) {
      console.error("Error saving data:", error);
      setModal({
        show: true,
        type: "error",
        title: "Error",
        message: `Failed to ${
          isEditMode ? "update" : "add"
        } product offer charges.`,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Find the selected product to get its ProductID
      const selectedProduct = productGradeList.find(
        (p) => p.ProductName === formData.product && p.Grade === formData.grade
      );

      if (!selectedProduct) {
        setModal({
          show: true,
          type: "error",
          title: "Error",
          message: "Selected product and grade combination not found.",
        });
        return;
      }

      const dataToSave = {
        ...formData,
        productId: selectedProduct.ProductID, // Store the actual ProductID
        ProductOfferChargesID: isEditMode
          ? editProductOfferChargesID
          : uuidv4(),
      };
      await saveFormData(dataToSave);
      setShowForm(false);
      setIsEditMode(false);
      setEditProductOfferChargesID(null);
      setSelectedRow(null);
      setFormData({
        category: "",
        product: "",
        grade: "",
        agropreneursCharges: "",
        sortingGradingCharges: "",
        financeOperationsCharges: "",
        minOfferQuantity: "",
      });
    } else {
      console.log("Validation failed");
    }
  };

  // Handle form reset
  const handleReset = () => {
    setFormData({
      category: "",
      product: "",
      grade: "",
      agropreneursCharges: "",
      sortingGradingCharges: "",
      financeOperationsCharges: "",
      minOfferQuantity: "",
    });
    setErrors({});
    setIsEditMode(false);
    setEditProductOfferChargesID(null);
    setSelectedRow(null);
    setShowForm(false);
    setProductList([]);
    setProductGradeList([]);
  };

  const searchProductOfferCharges = useCallback(
    async (searchValue, newRowStart = rowStart) => {
      setLoading(true);

      const body = {
        target: "listProductOfferChargeDetails",
        userOrder: "ProductID",
        rowStart: newRowStart,
        pageSize: pageSize,
        filterTerms: {
          ProductCategoryID: searchValue.searchCategory || "",
          ProductNamePartial: searchValue.searchProduct || "",
          Grade: searchValue.searchProductGrade || "",
        },
      };

      try {
        const response = await fetchData(body);
        const modifiedItems = (response.items || response).map((item) => ({
          ...item,
          id: item.ProductID, // Add id property with value from RequirementsID
        }));
        setProductOfferCharges(modifiedItems);
        setTotalItems(response.total || 0);
      } catch (error) {
        console.error("Error loading listProductOfferCharges: ", error);
        setProductOfferCharges([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [rowStart, pageSize, setLoading]
  );

  const handleSearch = (searchTerm) => {
    console.log("Searching for:", searchTerm);
    searchProductOfferCharges(searchTerm);
    setRowStart(0);
  };

  const handleRowClick = useCallback(async (row) => {
    console.log("selected row::", row);
    setSelectedRow(row);
    setIsEditMode(true);
    setEditProductOfferChargesID(row.ProductOfferChargesID);
    setShowForm(true);
  }, []);

  // Populate form data when selectedRow changes
  useEffect(() => {
    if (selectedRow && isEditMode) {
      // Set the category first
      setFormData({
        category: selectedRow.ProductCategoryID || "",
        product: selectedRow.ProductName || "",
        grade: selectedRow.Grade || "",
        agropreneursCharges: selectedRow.AgropreneursCharges?.toString() || "",
        sortingGradingCharges:
          selectedRow.SortingGradingCharges?.toString() || "",
        financeOperationsCharges:
          selectedRow.FinanceOperationsCharges?.toString() || "",
        minOfferQuantity: selectedRow.MinOfferQuantityMT?.toString() || "",
      });

      // Load products for the category
      if (selectedRow.ProductCategoryID) {
        loadProductList(selectedRow.ProductCategoryID);
      }

      // Load grades for the product
      if (selectedRow.ProductName) {
        const matchingProducts = allProductList.filter(
          (p) => p.ProductName === selectedRow.ProductName
        );
        setProductGradeList(matchingProducts);
      }
    }
  }, [selectedRow, isEditMode, loadProductList]);

  // Scroll to form when showForm is true
  useEffect(() => {
    if (showForm && formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [showForm]);

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);

        const groups = decodedToken["cognito:groups"] || [];
        if (!groups.includes("AMTPL_ADMINS")) {
          await Auth.signOut();
          navigate("/");
        }
        await getParameterFromManager("INR_TO_USD");
      } catch (error) {
        await Auth.signOut();
        navigate("/");
      }
    };

    checkUser();
    loadProductCategoryList();
  }, [navigate, loadProductCategoryList]);

  useEffect(() => {
    searchProductOfferCharges(searchValues);
  }, [searchProductOfferCharges, searchValues]);

  const handlePageChange = (page) => {
    const newRowStart = (page - 1) * pageSize;
    setRowStart(newRowStart);
    searchProductOfferCharges(searchValues, newRowStart);
  };

  const currentPage = Math.floor(rowStart / pageSize) + 1;

  const fields = [
    {
      name: "searchCategory",
      label: "Category",
      type: "dropdown",
      options: productCategoryList.map((item) => ({
        value: item.ProductCategoryID,
        label: item.CategoryName,
      })),
      value: searchValues.searchCategory,
      onChange: (value) => {
        setSearchValues((prev) => ({
          ...prev,
          searchCategory: value,
          searchProduct: "",
          searchProductGrade: "",
        }));
        // Reset product and grade lists when category changes
        setProductList([]);
        setProductGradeList([]);
        // Load products for selected category
        if (value) {
          loadProductList(value);
        }
      },
    },
    {
      name: "searchProduct",
      label: "Product",
      type: "dropdown",
      options: productList.map((item) => ({
        value: item.ProductName,
        label: item.ProductName,
      })),
      value: searchValues.searchProduct,
      onChange: (value) => {
        setSearchValues((prev) => ({
          ...prev,
          searchProduct: value,
          searchProductGrade: "",
        }));
        // Reset grade list when product changes
        setProductGradeList([]);
        const matchingProducts = allProductList.filter(
          (p) => p.ProductName === value
        );
        setProductGradeList(matchingProducts);
      },
    },
    {
      name: "searchProductGrade",
      label: "Product Grade",
      type: "dropdown",
      options: productGradeList.map((item) => ({
        value: item.Grade,
        label: item.Grade,
      })),
      value: searchValues.searchProductGrade,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchProductGrade: value }));
      },
    },
  ];

  const columns = [
    {
      id: "CategoryName",
      numeric: false,
      label: "Category",
      // renderCell: (row) => {
      //   const item = productCategoryList.find(
      //     (p) => p.ProductCategoryID === row.ProductCategoryID
      //   );
      //   return item ? item.CategoryName : "";
      // },
    },
    {
      id: "ProductName",
      numeric: false,
      label: "Product Name",
    },
    {
      id: "Grade",
      numeric: false,
      label: "Grade",
    },
    {
      id: "AgropreneursCharges",
      numeric: true,
      label: "Agropreneurs Charges (in %)",
    },
    {
      id: "SortingGradingCharges",
      numeric: true,
      label: "Sorting Grading Charges (in %)",
    },
    {
      id: "FinanceOperationsCharges",
      numeric: true,
      label: "Finance Operations Charges (in %)",
    },
    {
      id: "MinOfferQuantityMT",
      numeric: true,
      label: "Min Offer Quantity (in MT)",
    },
  ];
  const commonInputStyles = {
    "& .MuiInputBase-root": {
      height: "40px",
      fontSize: "14px",
      width: "100%",
      borderRadius: "8px", // Add border radius here
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px 14px",
      fontSize: "14px",
    },
    width: "100%", // Ensure FormControl takes full width
  };
  return (
    <div className="amtpl-dashboard">
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />

      <div className="content">
        <main className="main-content">
          <div className="search-filter4">
            <div className="title4">
              <div className="head-text">Product Offer Charges</div>
            </div>
            <div className="filter4">
              <SearchFilter fields={fields} onSearch={handleSearch} />
            </div>
            <div
              className="head-btn"
              style={{ display: "flex", gap: "10px", alignItems: "center" }}
            >
              <div
                className="submit-btn"
                onClick={() => {
                  setShowForm(!showForm);
                  setIsEditMode(false);
                  setFormData({
                    category: "",
                    product: "",
                    grade: "",
                    agropreneursCharges: "",
                    sortingGradingCharges: "",
                    financeOperationsCharges: "",
                    minOfferQuantity: "",
                  });
                  setProductList([]);
                  setProductGradeList([]);
                }}
              >
                Add
              </div>
            </div>
          </div>

          <DataTable
            columns={columns}
            data={productoffercharges}
            total={totalItems}
            title="Product Offer charges Information"
            rowsPerPage={pageSize}
            onRowClick={handleRowClick}
            onPageChange={handlePageChange}
            currentPage={currentPage}
            selectedRows={selectedRow ? [selectedRow.ProductID] : []}
          />

          {showForm && (
            <div className="box-main">
              <div className="table-header">
                <div className="header-text">
                  <div className="head-text">
                    {/* {isEditMode ? "Edit" : "Add"}  */}
                  </div>
                </div>
                <div className="head-btn">
                  <>
                    <div onClick={handleSubmit} className="submit-btn">
                      {isEditMode ? "Update" : "Submit"}
                    </div>
                    <div onClick={handleReset} className="submit-btn">
                      {isEditMode ? "Cancel" : "Reset"}
                    </div>
                  </>
                </div>
              </div>

              <form
                ref={formRef}
                onSubmit={handleSubmit}
                style={{
                  padding: "10px",
                  backgroundColor: "#f9f9f9",
                  borderRadius: "8px",
                }}
              >
                <div className="row">
                  {/* Category Dropdown */}
                  <div className="col-md-4">
                    <div className="mb-3">
                      <FormControl
                        sx={{ minWidth: "100%" }}
                        error={!!errors.category}
                      >
                        <Typography variant="body2">Select Category</Typography>
                        <Select
                          name="category"
                          value={formData.category}
                          onChange={handleChange}
                          displayEmpty
                          sx={{
                            ...commonInputStyles,
                            backgroundColor: "white",
                          }}
                          inputProps={{ "aria-label": "Select Category" }}
                        >
                          <MenuItem value="" disabled>
                            Select Category
                          </MenuItem>
                          {productCategoryList.map((category) => (
                            <MenuItem
                              key={category.ProductCategoryID}
                              value={category.ProductCategoryID}
                            >
                              {category.CategoryName}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.category && (
                          <Typography variant="caption" color="error">
                            {errors.category}
                          </Typography>
                        )}
                      </FormControl>
                    </div>
                  </div>

                  {/* Product Dropdown */}
                  <div className="col-md-4">
                    <div className="mb-3">
                      <FormControl
                        sx={{ minWidth: "100%" }}
                        error={!!errors.product}
                      >
                        <Typography variant="body2">Select Product</Typography>
                        <Select
                          name="product"
                          value={formData.product}
                          onChange={handleChange}
                          displayEmpty
                          sx={{
                            ...commonInputStyles,
                            backgroundColor: "white",
                          }}
                          inputProps={{ "aria-label": "Select Product" }}
                          disabled={!formData.category}
                        >
                          <MenuItem value="" disabled>
                            Select Product
                          </MenuItem>
                          {productList.map((product) => (
                            <MenuItem
                              key={product.ProductID}
                              value={product.ProductName}
                            >
                              {product.ProductName}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.product && (
                          <Typography variant="caption" color="error">
                            {errors.product}
                          </Typography>
                        )}
                      </FormControl>
                    </div>
                  </div>

                  {/* Grade Dropdown */}
                  <div className="col-md-4">
                    <div className="mb-3">
                      <FormControl
                        sx={{ minWidth: "100%" }}
                        error={!!errors.grade}
                      >
                        <Typography variant="body2">Select Grade</Typography>
                        <Select
                          name="grade"
                          value={formData.grade}
                          onChange={handleChange}
                          displayEmpty
                          sx={{
                            ...commonInputStyles,
                            backgroundColor: "white",
                          }}
                          inputProps={{ "aria-label": "Select Grade" }}
                          disabled={!formData.product}
                        >
                          <MenuItem value="" disabled>
                            Select Grade
                          </MenuItem>
                          {productGradeList.map((product) => (
                            <MenuItem
                              key={product.ProductID}
                              value={product.Grade}
                            >
                              {product.Grade}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.grade && (
                          <Typography variant="caption" color="error">
                            {errors.grade}
                          </Typography>
                        )}
                      </FormControl>
                    </div>
                  </div>
                </div>

                <div className="row">
                  {/* Agropreneurs Charges */}
                  <div className="col-md-4">
                    <div className="mb-3">
                      <Typography variant="body2">
                        Agropreneurs Charges (%)
                      </Typography>
                      <TextField
                        type="text"
                        name="agropreneursCharges"
                        value={formData.agropreneursCharges}
                        onChange={handleChange}
                        sx={{ ...commonInputStyles, backgroundColor: "white" }}
                        error={!!errors.agropreneursCharges}
                        helperText={errors.agropreneursCharges}
                      />
                    </div>
                  </div>

                  {/* Sorting & Grading Charges */}
                  <div className="col-md-4">
                    <div className="mb-3">
                      <Typography variant="body2">
                        Sorting & Grading Charges (%)
                      </Typography>
                      <TextField
                        type="text"
                        name="sortingGradingCharges"
                        value={formData.sortingGradingCharges}
                        onChange={handleChange}
                        sx={{ ...commonInputStyles, backgroundColor: "white" }}
                        error={!!errors.sortingGradingCharges}
                        helperText={errors.sortingGradingCharges}
                      />
                    </div>
                  </div>

                  {/* Finance & Operations Charges */}
                  <div className="col-md-4">
                    <div className="mb-3">
                      <Typography variant="body2">
                        Finance & Operations Charges (%)
                      </Typography>
                      <TextField
                        type="text"
                        name="financeOperationsCharges"
                        value={formData.financeOperationsCharges}
                        onChange={handleChange}
                        sx={{ ...commonInputStyles, backgroundColor: "white" }}
                        error={!!errors.financeOperationsCharges}
                        helperText={errors.financeOperationsCharges}
                      />
                    </div>
                  </div>

                  {/* Min Offer Quantity MT */}
                  <div className="col-md-4">
                    <div className="mb-3">
                      <Typography variant="body2">
                        Min Offer Quantity (MT)
                      </Typography>
                      <TextField
                        type="text"
                        name="minOfferQuantity"
                        value={formData.minOfferQuantity}
                        onChange={handleChange}
                        sx={{ ...commonInputStyles, backgroundColor: "white" }}
                        error={!!errors.minOfferQuantity}
                        helperText={errors.minOfferQuantity}
                      />
                    </div>
                  </div>
                </div>
              </form>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default TransferofferCharges;
